/**
 * Parses human-written reservation date/time strings as venue wall-clock
 * (UTC civil digits). Mirrors iOS HumanReservationDateTime (#87).
 * Uses clues for M/d vs d/M — never Locale alone.
 */

import { isoUtcWallClock, ymdFromUnknown } from './wallClock';

export type SlashOrder = 'monthDay' | 'dayMonth';

export interface HumanReservationTripStay {
  cityName: string;
  startYmd: string;
  endYmd: string;
}

export interface HumanReservationClues {
  timeZoneIdentifier?: string | null;
  locationText?: string | null;
  siblingDateStrings?: string[];
  emailHints?: string | null;
  tripStays?: HumanReservationTripStay[];
}

const MONTHS: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

function expandYear(year: number): number {
  if (year >= 100) return year;
  return year >= 70 ? 1900 + year : 2000 + year;
}

function parseTimeOfDay(raw: string): { hour: number; minute: number } | null {
  const trimmed = raw.trim();
  const ampm = /\b(am|pm)\b/i.exec(trimmed);
  const hm = /(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(trimmed);
  if (!hm) return null;
  let hour = Number(hm[1]);
  const minute = Number(hm[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (ampm) {
    const isPm = ampm[1].toLowerCase() === 'pm';
    if (hour === 12) hour = isPm ? 12 : 0;
    else if (isPm) hour += 12;
  }
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

function splitDateAndTime(raw: string): { date: string; time: string | null } {
  const isoSplit = raw.match(/^(\d{4}-\d{2}-\d{2})[ T](.+)$/);
  if (isoSplit) return { date: isoSplit[1], time: isoSplit[2] };

  const ampmIdx = raw.search(/\d{1,2}:\d{2}/);
  if (ampmIdx > 0) {
    return {
      date: raw.slice(0, ampmIdx).trim().replace(/,$/, ''),
      time: raw.slice(ampmIdx).trim(),
    };
  }
  return { date: raw, time: null };
}

function prefersDayMonth(clues: HumanReservationClues): boolean {
  const blob = [
    clues.timeZoneIdentifier,
    clues.locationText,
    clues.emailHints,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (
    blob.includes('america/') ||
    blob.includes('united states') ||
    blob.includes('usa')
  ) {
    return false;
  }

  const dayMonthHints = [
    'europe/',
    'asia/',
    'africa/',
    'australia/',
    'pacific/auckland',
    'italy',
    'france',
    'germany',
    'spain',
    'portugal',
    'uk',
    'united kingdom',
    'ireland',
    'netherlands',
    'belgium',
    'switzerland',
    'austria',
    'greece',
    'sicily',
    'paris',
    'rome',
    'london',
    'berlin',
    'madrid',
    'lisbon',
    'noto',
  ];
  return dayMonthHints.some((h) => blob.includes(h));
}

function prefersMonthDay(clues: HumanReservationClues): boolean {
  const blob = [
    clues.timeZoneIdentifier,
    clues.locationText,
    clues.emailHints,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (
    blob.includes('america/') ||
    blob.includes('united states') ||
    blob.includes('usa')
  ) {
    return true;
  }
  const monthDayHints = [
    'canada',
    'chicago',
    'new york',
    'los angeles',
    'houston',
  ];
  return monthDayHints.some((h) => blob.includes(h));
}

function cityMatchesLocation(cityName: string, location: string): boolean {
  const city = cityName.trim().toLowerCase();
  const loc = location.toLowerCase();
  if (!city) return false;
  if (loc.includes(city) || city.includes(loc)) return true;
  return loc.split(',').some((part) => part.trim().toLowerCase() === city);
}

function staysMatching(
  ymd: string,
  stays: HumanReservationTripStay[]
): HumanReservationTripStay[] {
  return stays.filter((stay) => ymd >= stay.startYmd && ymd <= stay.endYmd);
}

function ymdFromParts(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() + 1 !== month ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function slashOrderFromTripStays(
  first: number,
  second: number,
  year: number,
  clues: HumanReservationClues
): SlashOrder | null {
  const stays = clues.tripStays ?? [];
  if (stays.length === 0) return null;
  const md = ymdFromParts(year, first, second);
  const dm = ymdFromParts(year, second, first);
  if (!md || !dm) return null;

  const mdMatches = staysMatching(md, stays);
  const dmMatches = staysMatching(dm, stays);
  const location = clues.locationText?.trim() ?? '';

  if (location) {
    const mdCity = mdMatches.filter((s) => cityMatchesLocation(s.cityName, location));
    const dmCity = dmMatches.filter((s) => cityMatchesLocation(s.cityName, location));
    if (mdCity.length > 0 && dmCity.length === 0) return 'monthDay';
    if (dmCity.length > 0 && mdCity.length === 0) return 'dayMonth';
  }

  if (mdMatches.length > 0 && dmMatches.length === 0) return 'monthDay';
  if (dmMatches.length > 0 && mdMatches.length === 0) return 'dayMonth';
  return null;
}

function resolveSlashOrder(
  first: number,
  second: number,
  year: number,
  clues: HumanReservationClues
): SlashOrder | null {
  const firstIsMonth = first >= 1 && first <= 12;
  const secondIsMonth = second >= 1 && second <= 12;

  if (first > 12 && secondIsMonth) return 'dayMonth';
  if (second > 12 && firstIsMonth) return 'monthDay';
  if (!firstIsMonth || !secondIsMonth) return null;

  const fromTrips = slashOrderFromTripStays(first, second, year, clues);
  if (fromTrips) return fromTrips;

  if (prefersDayMonth(clues)) return 'dayMonth';
  if (prefersMonthDay(clues)) return 'monthDay';
  return 'monthDay';
}

function parseNamedMonth(raw: string): string | null {
  const normalized = raw.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
  const parts = splitDateAndTime(normalized);
  const datePart = parts.date;

  // "28 Aug 2028" or "August 28, 2028"
  const euro =
    /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{2,4})$/.exec(datePart) ??
    /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{2,4})\b/.exec(normalized);
  const us =
    /^([A-Za-z]+)\s+(\d{1,2})\s+(\d{2,4})$/.exec(datePart) ??
    /^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{2,4})$/.exec(datePart);

  let year: number;
  let month: number;
  let day: number;

  if (euro) {
    day = Number(euro[1]);
    month = MONTHS[euro[2].toLowerCase()] ?? 0;
    year = expandYear(Number(euro[3]));
  } else if (us) {
    month = MONTHS[us[1].toLowerCase()] ?? 0;
    day = Number(us[2]);
    year = expandYear(Number(us[3]));
  } else {
    return null;
  }

  const ymd = ymdFromParts(year, month, day);
  if (!ymd) return null;
  const time = parts.time ? parseTimeOfDay(parts.time) : { hour: 0, minute: 0 };
  if (!time) return null;
  return isoUtcWallClock(
    ymd,
    `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`
  );
}

function parseNumericDateTime(
  raw: string,
  clues: HumanReservationClues
): string | null {
  const normalized = raw.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
  const parts = splitDateAndTime(normalized);
  const datePart = parts.date;
  const tokens = datePart
    .split(/[/\-.]/)
    .map((t) => t.trim())
    .filter(Boolean);
  if (tokens.length !== 3) return null;
  const a = Number(tokens[0]);
  const b = Number(tokens[1]);
  const yRaw = Number(tokens[2]);
  if (![a, b, yRaw].every(Number.isFinite)) return null;
  const year = expandYear(yRaw);

  const order = resolveSlashOrder(a, b, year, clues);
  if (!order) return null;
  const month = order === 'monthDay' ? a : b;
  const day = order === 'monthDay' ? b : a;
  const ymd = ymdFromParts(year, month, day);
  if (!ymd) return null;

  const time = parts.time ? parseTimeOfDay(parts.time) : { hour: 0, minute: 0 };
  if (!time) return null;
  return isoUtcWallClock(
    ymd,
    `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`
  );
}

/**
 * Returns UTC civil ISO `YYYY-MM-DDTHH:mm:ssZ` or null.
 * Naive ISO with Z keeps the written digits (Noto 11:30 stays 11:30).
 */
export function parseHumanReservationDateTime(
  raw: string,
  clues: HumanReservationClues = {}
): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Device Sync / Zulu: treat Z as wall-clock digits, not an absolute instant.
  const zulu = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::\d{2})?Z$/i.exec(trimmed);
  if (zulu) {
    return isoUtcWallClock(zulu[1], zulu[2]);
  }

  // Naive ISO local (no Z)
  const naiveIso =
    /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::\d{2})?$/.exec(trimmed);
  if (naiveIso) {
    return isoUtcWallClock(naiveIso[1], naiveIso[2]);
  }

  // Plain YMD
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return isoUtcWallClock(trimmed, '00:00');
  }

  const named = parseNamedMonth(trimmed);
  if (named) return named;

  return parseNumericDateTime(trimmed, clues);
}

export function tripStaysFromCityMeta(
  meta: Record<string, { arrivalDate?: string | null; departureDate?: string | null }>,
  cityNames: Record<string, string>
): HumanReservationTripStay[] {
  const stays: HumanReservationTripStay[] = [];
  for (const [id, visit] of Object.entries(meta)) {
    const startYmd = ymdFromUnknown(visit.arrivalDate);
    const endYmd = ymdFromUnknown(visit.departureDate) ?? startYmd;
    if (!startYmd || !endYmd) continue;
    stays.push({
      cityName: cityNames[id] ?? id,
      startYmd,
      endYmd,
    });
  }
  return stays;
}
